import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ContentService, Content, ListContentRequest, UploadRequest, UploadProgress, ContentSearchParams } from '../api';
import { toast } from 'react-hot-toast';

export const useContentList = (params: ListContentRequest = {}) => {
  return useQuery({
    queryKey: ['content', 'list', params],
    queryFn: () => ContentService.listContent(params),
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: false,
  });
};

export const useRecentContent = (limit: number = 10) => {
  return useQuery({
    queryKey: ['content', 'recent', limit],
    queryFn: () => ContentService.getRecentContent(limit),
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
  });
};

export const useContent = (id: string, enabled = true) => {
  return useQuery({
    queryKey: ['content', id],
    queryFn: () => ContentService.getContent(id),
    enabled: enabled && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useContentUpload = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      uploadRequest,
      onProgress,
    }: {
      uploadRequest: UploadRequest;
      onProgress?: (progress: UploadProgress) => void;
    }) => ContentService.uploadContent(uploadRequest, onProgress),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['content'] });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
      toast.success(`Upload successful: ${data.filename}`);
    },
    onError: (error: any) => {
      console.error('Upload failed:', error);
      toast.error(`Upload failed: ${error.response?.data?.message || error.message}`);
    },
  });
};

export const useContentUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Content> }) =>
      ContentService.updateContent(id, updates),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['content', data.id] });
      queryClient.invalidateQueries({ queryKey: ['content', 'list'] });
      toast.success('Content updated successfully');
    },
    onError: (error: any) => {
      console.error('Update failed:', error);
      toast.error(`Update failed: ${error.response?.data?.message || error.message}`);
    },
  });
};

export const useContentDelete = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ContentService.deleteContent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content'] });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
      toast.success('Content deleted successfully');
    },
    onError: (error: any) => {
      console.error('Delete failed:', error);
      toast.error(`Delete failed: ${error.response?.data?.message || error.message}`);
    },
  });
};

export const useContentApproval = () => {
  const queryClient = useQueryClient();

  const approveMutation = useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      ContentService.approveContent(id, comment),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['content', data.id] });
      queryClient.invalidateQueries({ queryKey: ['content', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
      toast.success('Content approved successfully');
    },
    onError: (error: any) => {
      console.error('Approval failed:', error);
      toast.error(`Approval failed: ${error.response?.data?.message || error.message}`);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      ContentService.rejectContent(id, reason),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['content', data.id] });
      queryClient.invalidateQueries({ queryKey: ['content', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
      toast.success('Content rejected');
    },
    onError: (error: any) => {
      console.error('Rejection failed:', error);
      toast.error(`Rejection failed: ${error.response?.data?.message || error.message}`);
    },
  });

  return {
    approve: approveMutation.mutate,
    reject: rejectMutation.mutate,
    isApproving: approveMutation.isPending,
    isRejecting: rejectMutation.isPending,
  };
};

export const useContentBulkOperations = () => {
  const queryClient = useQueryClient();

  const bulkApproveMutation = useMutation({
    mutationFn: ContentService.bulkApprove,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content'] });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
      toast.success('Content bulk approved successfully');
    },
    onError: (error: any) => {
      console.error('Bulk approve failed:', error);
      toast.error(`Bulk approve failed: ${error.response?.data?.message || error.message}`);
    },
  });

  const bulkRejectMutation = useMutation({
    mutationFn: ({ ids, reason }: { ids: string[]; reason: string }) =>
      ContentService.bulkReject(ids, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content'] });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
      toast.success('Content bulk rejected successfully');
    },
    onError: (error: any) => {
      console.error('Bulk reject failed:', error);
      toast.error(`Bulk reject failed: ${error.response?.data?.message || error.message}`);
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: ContentService.bulkDelete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content'] });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
      toast.success('Content bulk deleted successfully');
    },
    onError: (error: any) => {
      console.error('Bulk delete failed:', error);
      toast.error(`Bulk delete failed: ${error.response?.data?.message || error.message}`);
    },
  });

  return {
    bulkApprove: bulkApproveMutation.mutate,
    bulkReject: bulkRejectMutation.mutate,
    bulkDelete: bulkDeleteMutation.mutate,
    isBulkApproving: bulkApproveMutation.isPending,
    isBulkRejecting: bulkRejectMutation.isPending,
    isBulkDeleting: bulkDeleteMutation.isPending,
  };
};

export const useContentMetadata = (id: string, enabled = true) => {
  return useQuery({
    queryKey: ['content', id, 'metadata'],
    queryFn: () => ContentService.getMetadata(id),
    enabled: enabled && !!id,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useSearchSuggestions = (query: string) => {
  return useQuery({
    queryKey: ['content', 'search', 'suggestions', query],
    queryFn: () => ContentService.getSearchSuggestions(query),
    enabled: !!query && query.length >= 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useContentStats = () => {
  return useQuery({
    queryKey: ['content', 'stats'],
    queryFn: ContentService.getContentStats,
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
    staleTime: 15 * 1000, // 15 seconds
  });
};

// Advanced content search with full-text search and extended filters
export const useContentSearch = (params: ContentSearchParams, enabled = true) => {
  return useQuery({
    queryKey: ['content', 'search', params],
    queryFn: () => ContentService.searchContent(params),
    enabled: enabled,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData, // Keep previous data while loading
  });
};